import type { CommunicationInput, LocalCommunication } from '@iculogbook/shared';
import { uuidv7 } from 'uuidv7';

import { db } from '../db.ts';
import { ensurePersistentStorage } from '../persist.ts';
import {
  byOccurredAtDescending,
  isLive,
  notifyLocalWrite,
  outboxItem,
  userRange,
  writeWithOutbox,
} from './internal.ts';

/**
 * All reads and writes of Communication entries go through here, on exactly
 * the same terms as every other entry: components never touch Dexie, every
 * write is paired with an outbox item in one transaction, and nothing is
 * ever hard deleted.
 *
 * Simpler than `casesRepository`, on the same terms as `edtRepository`: a
 * Communication is standalone in this phase, not nestable inside a Case, so
 * there is no nesting to keep atomic and no cascading delete.
 */

export const communicationsRepository = {
  /** Writes a new entry and returns immediately. The network is not involved. */
  async create(userId: string, input: CommunicationInput): Promise<LocalCommunication> {
    const now = new Date();

    const record: LocalCommunication = {
      ...input,
      id: uuidv7(),
      userId,
      source: 'manual',
      createdAt: now,
      updatedAt: now,
      syncedAt: null,
    };

    await writeWithOutbox('communication', record.id, userId, async () => {
      await db.communications.add(record);
    });

    void ensurePersistentStorage();

    return record;
  },

  async update(userId: string, id: string, changes: Partial<CommunicationInput>): Promise<void> {
    const existing = await db.communications.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    await writeWithOutbox('communication', id, userId, async () => {
      await db.communications.update(id, { ...changes, updatedAt: new Date(), syncedAt: null });
    });
  },

  /** Soft delete only. Nothing here removes a row. */
  async softDelete(userId: string, id: string): Promise<void> {
    const existing = await db.communications.get(id);
    if (existing === undefined || !isLive(existing, userId)) return;

    const now = new Date();

    await writeWithOutbox('communication', id, userId, async () => {
      await db.communications.update(id, { deletedAt: now, updatedAt: now, syncedAt: null });
    });
  },

  async restore(userId: string, id: string): Promise<void> {
    const existing = await db.communications.get(id);
    if (existing === undefined || existing.userId !== userId) return;

    await writeWithOutbox('communication', id, userId, async () => {
      await db.communications.update(id, {
        deletedAt: undefined,
        updatedAt: new Date(),
        syncedAt: null,
      });
    });
  },

  /**
   * Writes entries back from an export, in one transaction, each with its own
   * outbox item so the restored logbook reaches the server too. Ids are
   * preserved, which is what makes a restore idempotent.
   */
  async restoreFromExport(userId: string, records: readonly LocalCommunication[]): Promise<number> {
    if (records.length === 0) return 0;

    await db.transaction('rw', db.communications, db.outbox, async () => {
      for (const record of records) {
        await db.communications.put({
          ...record,
          userId,
          source: 'restore',
          updatedAt: record.updatedAt,
          syncedAt: null,
        });
        await db.outbox.add(outboxItem('communication', record.id, userId));
      }
    });

    notifyLocalWrite();
    void ensurePersistentStorage();

    return records.length;
  },

  async get(userId: string, id: string): Promise<LocalCommunication | undefined> {
    const record = await db.communications.get(id);
    return record !== undefined && isLive(record, userId) ? record : undefined;
  },

  /** Newest first. Excludes deleted rows and anything belonging to another user. */
  async list(userId: string): Promise<LocalCommunication[]> {
    const [lower, upper] = userRange(userId);
    const records = await db.communications
      .where('[userId+occurredAt]')
      .between(lower, upper, true, true)
      .toArray();

    return records.filter((record) => isLive(record, userId)).sort(byOccurredAtDescending);
  },

  /** Soft-deleted rows, for Settings → Recently deleted. */
  async listDeleted(userId: string): Promise<LocalCommunication[]> {
    const records = await db.communications.toArray();

    return records
      .filter((record) => record.userId === userId && record.deletedAt !== undefined)
      .sort(byOccurredAtDescending);
  },

  /**
   * An unsaved copy, dated now — "another conversation like that one".
   * Nothing is written; the copy becomes a record only when `create` is
   * called with it.
   */
  async duplicate(userId: string, id: string): Promise<CommunicationInput | undefined> {
    const source = await this.get(userId, id);
    if (source === undefined) return undefined;

    return {
      category: source.category,
      occurredAt: new Date(),
      ageBand: source.ageBand,
      capabilityLevel: source.capabilityLevel,
      // Arrays are copied, not shared, or editing the duplicate would silently
      // edit the original's chips too.
      ...(source.withWhom === undefined ? {} : { withWhom: [...source.withWhom] }),
      ...(source.outcome === undefined ? {} : { outcome: source.outcome }),
      // Who was taught is a fact about this conversation, not the next one —
      // the same reasoning a procedure's teachingGrades follows.
      ...(source.hillos === undefined ? {} : { hillos: [...source.hillos] }),
      ...(source.note === undefined ? {} : { note: source.note }),
    };
  },
};
